import type { Task } from "../types";

export const isInstallationType = (type?: string | null) =>
  ["installation", "монтаж", "montazh"].includes((type ?? "").toLowerCase());

export const isInstallation = (
  task: Pick<Task, "type" | "installation_start" | "installation_end">,
) =>
  task.type === "installation" &&
  !!task.installation_start &&
  !!task.installation_end;

export const installationRangeLabel = (
  task: Pick<Task, "installation_start" | "installation_end">,
) =>
  [task.installation_start, task.installation_end]
    .map((date) => date?.split("-").reverse().join("."))
    .join(" — ");

export function validDate(value?: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return (
    Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

export function normalizeInstallation<T extends Partial<Task>>(
  task: T,
  projectName?: string,
): T {
  if (!isInstallationType(task.type))
    return { ...task, installation_start: null, installation_end: null };
  if (task.deal_id == null) throw new Error("Выберите проект для монтажа");
  if (
    !validDate(task.installation_start) ||
    !validDate(task.installation_end) ||
    task.installation_end < task.installation_start
  )
    throw new Error(
      "Укажите начало и окончание монтажа: последний день включительно",
    );
  return {
    ...task,
    type: "installation",
    team_task: true,
    sales_id: null,
    contact_id: null,
    admin_only: false,
    text: projectName ? `Монтаж «${projectName}»` : "Монтаж",
    due_date: null,
    scheduled_start: null,
    scheduled_end: null,
    result: null,
  } as T;
}

export const installationOnDay = (task: Task, date: string) =>
  isInstallation(task) &&
  !!task.installation_start &&
  !!task.installation_end &&
  task.installation_start <= date &&
  task.installation_end >= date;

// Each interval receives a stable lane for the whole week, including overlaps.
export function installationLanes(tasks: Task[], first: string, last: string) {
  const ends: string[] = [];
  return tasks
    .filter(
      (t) =>
        isInstallation(t) &&
        t.installation_start! <= last &&
        t.installation_end! >= first,
    )
    .sort(
      (a, b) =>
        a.installation_start!.localeCompare(b.installation_start!) ||
        String(a.id).localeCompare(String(b.id)),
    )
    .map((task) => {
      const start =
        task.installation_start! < first ? first : task.installation_start!;
      let lane = ends.findIndex((end) => end < start);
      if (lane < 0) lane = ends.length;
      ends[lane] = task.installation_end!;
      return { task, lane };
    });
}
